import React from 'react';
import styled from 'styled-components';
import { Dropdown, Tooltip } from 'antd';
import { EllipsisOutlined } from '@ant-design/icons';

import { LinkButton } from '@/components/ui/Link';
import AccountAvatarFace from '@/components/ui/AccountAvatarFace';
import {
    FeatureAccountFooterEmail,
    FeatureAccountFooterInfo,
    FeatureAccountFooterName,
    FeatureAccountFooterMoreIcon,
    FeatureAccountFooterTrigger,
    ModuleRailAvatarWrap,
    ModuleRailOnlineDot,
} from '@/components/layout/insideShell.styles';
import {
    AvatarWithBadge,
    UserAvatar,
    UserDropdownAvatar,
    UserDropdownDivider,
    UserDropdownHeader,
    UserDropdownInfo,
    UserDropdownMenuWrap,
    UserDropdownMeta,
    UserDropdownName,
    UserDropdownPanel,
} from '@/layout/style';

const DropdownIdLink = styled(LinkButton)`
    transform: translateX(6px);
    transition: transform 0.2s ease, text-decoration-color 0.2s ease;

    &:hover,
    &:focus-visible {
        transform: translateX(0);
        text-decoration: underline;
        text-underline-offset: 2px;
    }
`;

/**
 * Account dropdown (shared panel) — module rail uses compact avatar; feature sidebar uses name + email + ⋯ row.
 */
export function InsideUserMenu({
    variant = 'rail',
    anchor = 'rail',
    userMenuOpen,
    userMenuAnchor,
    setUserMenuOpen,
    setUserMenuAnchor,
    user,
    userRole,
    userMenuItems,
    handleUserMenuClick,
    getUserInitials,
    getUserName,
    currentUserAvatarColor,
    currentUserAvatarUrl,
    copyValue,
}) {
    const open = userMenuOpen && userMenuAnchor === anchor;
    const onOpenChange = (next) => {
        setUserMenuOpen(next);
        if (next) setUserMenuAnchor(anchor);
        else setUserMenuAnchor(null);
    };

    const emailLine = user?.email || '—';

    const trigger =
        variant === 'feature' ? (
            <FeatureAccountFooterTrigger aria-label={`Account menu, ${getUserName()}`}>
                <FeatureAccountFooterInfo>
                    <FeatureAccountFooterName>{getUserName()}</FeatureAccountFooterName>
                    <FeatureAccountFooterEmail>{emailLine}</FeatureAccountFooterEmail>
                </FeatureAccountFooterInfo>
                <FeatureAccountFooterMoreIcon aria-hidden>
                    <EllipsisOutlined rotate={90} />
                </FeatureAccountFooterMoreIcon>
            </FeatureAccountFooterTrigger>
        ) : (
            <ModuleRailAvatarWrap aria-label={`Account menu, ${getUserName()}`}>
                <AvatarWithBadge>
                    <UserAvatar
                        $collapsed={false}
                        $size="40px"
                        $radius="50%"
                        $avatarBg={currentUserAvatarUrl ? 'transparent' : currentUserAvatarColor.bg}
                        $avatarColor={currentUserAvatarUrl ? 'transparent' : currentUserAvatarColor.color}
                        style={{
                            cursor: 'pointer',
                            border: currentUserAvatarUrl ? '1px solid var(--ant-color-border-secondary, #e5e7eb)' : 'none',
                        }}
                    >
                        <AccountAvatarFace
                            imageUrl={currentUserAvatarUrl}
                            initials={getUserInitials()}
                            alt={getUserName()}
                        />
                    </UserAvatar>
                </AvatarWithBadge>
                <ModuleRailOnlineDot aria-hidden />
            </ModuleRailAvatarWrap>
        );

    return (
        <Dropdown
            open={open}
            onOpenChange={onOpenChange}
            menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
            placement="topRight"
            trigger={['click']}
            popupRender={(origin) => (
                <UserDropdownPanel>
                    <UserDropdownHeader>
                        <UserDropdownAvatar
                            $avatarBg={currentUserAvatarUrl ? 'transparent' : currentUserAvatarColor.bg}
                            $avatarColor={currentUserAvatarUrl ? 'transparent' : currentUserAvatarColor.color}
                            style={{
                                border: currentUserAvatarUrl ? '1px solid var(--ant-color-border-secondary, #e5e7eb)' : 'none',
                            }}
                        >
                            <AccountAvatarFace
                                imageUrl={currentUserAvatarUrl}
                                initials={getUserInitials()}
                                alt={getUserName()}
                            />
                        </UserDropdownAvatar>
                        <UserDropdownInfo>
                            <UserDropdownName>{getUserName()}</UserDropdownName>
                            <UserDropdownMeta>
                                {[user?.email || null, user?.role || user?.roles?.[0] || userRole]
                                    .filter(Boolean)
                                    .join(' · ') || '—'}
                            </UserDropdownMeta>
                        </UserDropdownInfo>
                    </UserDropdownHeader>
                    <UserDropdownDivider />
                    <div
                        style={{
                            padding: '10px 14px',
                            display: 'grid',
                            rowGap: 3,
                            columnGap: 1,
                            gridTemplateColumns: '84px minmax(0, 1fr)',
                            alignItems: 'center',
                        }}
                    >
                        {user?.companyId ? (
                            <>
                                <div style={{ minWidth: 0 }}>
                                    <UserDropdownMeta style={{ margin: 0, lineHeight: 1.4 }}>Company ID</UserDropdownMeta>
                                </div>
                                <Tooltip title="Click to copy">
                                    <span
                                        style={{
                                            minWidth: 0,
                                            maxWidth: '100%',
                                            overflow: 'hidden',
                                            whiteSpace: 'nowrap',
                                            textOverflow: 'ellipsis',
                                            display: 'block',
                                        }}
                                    >
                                        <DropdownIdLink
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                copyValue(user?.companyId);
                                            }}
                                            style={{
                                                fontSize: 12,
                                                padding: 0,
                                                height: 'auto',
                                                maxWidth: '100%',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                display: 'inline-block',
                                                verticalAlign: 'middle',
                                            }}
                                        >
                                            {user?.companyId}
                                        </DropdownIdLink>
                                    </span>
                                </Tooltip>
                            </>
                        ) : null}
                        {user?.publicId ? (
                            <>
                                <div style={{ minWidth: 0 }}>
                                    <UserDropdownMeta style={{ margin: 0, lineHeight: 1.4 }}>Public ID</UserDropdownMeta>
                                </div>
                                <Tooltip title="Click to copy">
                                    <span
                                        style={{
                                            minWidth: 0,
                                            maxWidth: '100%',
                                            overflow: 'hidden',
                                            whiteSpace: 'nowrap',
                                            textOverflow: 'ellipsis',
                                            display: 'block',
                                        }}
                                    >
                                        <DropdownIdLink
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                copyValue(user?.publicId);
                                            }}
                                            style={{
                                                fontSize: 12,
                                                padding: 0,
                                                height: 'auto',
                                                maxWidth: '100%',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                display: 'inline-block',
                                                verticalAlign: 'middle',
                                            }}
                                        >
                                            {user?.publicId}
                                        </DropdownIdLink>
                                    </span>
                                </Tooltip>
                            </>
                        ) : null}
                    </div>
                    <UserDropdownDivider />
                    <UserDropdownMenuWrap>{origin}</UserDropdownMenuWrap>
                </UserDropdownPanel>
            )}
        >
            {trigger}
        </Dropdown>
    );
}
